import type { ApiErrorBody } from "@xop/dashboard-shared";

import { getClientVersion } from "./clientVersion.js";
import { env } from "./env.js";
import { notifySessionLost } from "./sessionEvents.js";
import { tokenStore } from "./tokenStore.js";

/**
 * Single fetch wrapper for the dashboard server.
 *
 * Responsibilities:
 *   - Attach the bearer token + JSON content-type.
 *   - Handle 401 → one refresh attempt → retry once → bail to "logged out".
 *   - Surface ApiErrorBody as a typed `ApiError` so callers can branch on
 *     `code` instead of regexing English error strings.
 *
 * Refresh contention is solved with a singleton in-flight promise: any
 * 401 that lands while a refresh is already running awaits the same
 * promise instead of stampeding the server.
 *
 * A FAILED REFRESH IS NOT AUTOMATICALLY A DEAD SESSION
 * (docs/session-lifetime.md). This used to be a boolean — anything other
 * than a clean 200 meant "logged out", so a laptop waking with the wifi
 * still down, a deploy restarting the server, or a 502 from Caddy threw
 * the user to /login and destroyed a refresh chain that was good for a
 * year. Refreshing is now tri-state: only an upstream that actively
 * REJECTS us ends the session; transport failures, 5xx and rate limits
 * leave the tokens alone so the next attempt (or `sessionKeepAlive`)
 * picks the session straight back up.
 */

export type RefreshOutcome =
  /** Fresh tokens stored. */
  | "ok"
  /** Couldn't reach / couldn't get an answer. Session is still assumed good. */
  | "transient"
  /** The server rejected the refresh token itself. Session is over. */
  | "dead";

let refreshInFlight: Promise<RefreshOutcome> | null = null;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly details: unknown;
  /**
   * The permission key crm-api/dashboard-server refused, when the body
   * carried one — either as a top-level `permission` field (the
   * dashboard server's `upstreamPermissionDetail` lifts it there for a
   * 422 `unknown_permission`) or, failing that, one layer down inside
   * `details.upstream_body.permission` (crm-api's raw proxied body).
   * `undefined` for every error body that doesn't carry it.
   */
  readonly permission: string | undefined;

  constructor(message: string, status: number, code?: string, details?: unknown, permission?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.permission = permission;
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip auto-attaching the bearer token (used by /auth/login itself). */
  anonymous?: boolean;
  /** Swallow 401 → refresh → retry once. Default true. */
  retryOnAuthError?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { anonymous = false, retryOnAuthError = true, body, headers, ...rest } = options;

  // Only declare a JSON content-type when we're actually sending a JSON
  // body. Setting `Content-Type: application/json` with an empty body
  // made Fastify on the dashboard server reject the request up front
  // with `FST_ERR_CTP_EMPTY_JSON_BODY` — which broke single-doc DELETE
  // (no body) even though the route handler was perfectly happy. The
  // browser's fetch will pick a sensible default (or none) when the
  // header is omitted.
  const jsonHeader = body !== undefined ? { "content-type": "application/json" } : {};

  const init: RequestInit = {
    ...rest,
    headers: {
      ...jsonHeader,
      ...clientHeader(),
      ...(anonymous ? {} : authHeader()),
      ...(headers ?? {}),
    },
    credentials: "include",
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const url = path.startsWith("http") ? path : `${env.apiUrl}${path}`;
  const res = await fetch(url, init);

  if (res.status === 401 && !anonymous && retryOnAuthError) {
    const refreshed = await sharedRefresh();
    if (refreshed === "ok") {
      const init2: RequestInit = {
        ...rest,
        headers: {
          ...jsonHeader,
          ...clientHeader(),
          ...authHeader(),
          ...(headers ?? {}),
        },
        credentials: "include",
      };
      if (body !== undefined) init2.body = JSON.stringify(body);
      const retryRes = await fetch(url, init2);
      return parseResponse<T>(retryRes);
    }
    // The refresh token itself was rejected → the session is genuinely
    // dead. Tell the rest of the app so the AuthProvider clears state and
    // `<RequireAuth>` bounces to /login on the next render. Without this
    // signal, components receive a thrown ApiError but the chrome above
    // them keeps rendering with a stale `user` and the user sees
    // "undefined" in the header instead of being redirected.
    //
    // "transient" deliberately falls through: the caller gets the 401 as
    // an ApiError, but the SESSION survives so the next attempt recovers.
    if (refreshed === "dead") {
      notifySessionLost({ source: "dashboard-api", status: 401 });
    }
  }

  return parseResponse<T>(res);
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? safeJson(text) : null;

  if (!res.ok) {
    const err = body as ApiErrorBody | null;
    throw new ApiError(
      err?.error ?? res.statusText ?? `HTTP ${res.status}`,
      res.status,
      err?.code,
      err?.details,
      extractPermission(err),
    );
  }
  return body as T;
}

/**
 * Pull the refused permission key out of an error body. Some routes (e.g.
 * the reseller-accounts 422 `unknown_permission` response) lift crm-api's
 * `permission` field to the top level; if that's absent, fall back to
 * `details.upstream_body.permission`, where the raw proxied crm-api body
 * still carries it.
 */
function extractPermission(err: ApiErrorBody | null): string | undefined {
  const top = (err as { permission?: unknown } | null)?.permission;
  if (typeof top === "string" && top.length > 0) return top;

  const nested = (err?.details as { upstream_body?: { permission?: unknown } } | undefined)?.upstream_body
    ?.permission;
  return typeof nested === "string" && nested.length > 0 ? nested : undefined;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function authHeader(): Record<string, string> {
  const token = tokenStore.getAccess();
  return token ? { authorization: `Bearer ${token}` } : {};
}

/** Reports the running client (desktop app version, or "web") so the server
 * can record which build each user is on. Sent on every request. */
function clientHeader(): Record<string, string> {
  return { "x-client-version": getClientVersion() };
}

/** How long to wait for another tab's rotation to land in localStorage
 * before deciding a rejected refresh really means "logged out". */
const ROTATION_RACE_WAIT_MS = 400;

async function sharedRefresh(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      return await attemptRefresh(1);
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * One refresh round-trip, with a bounded retry for the cross-tab rotation
 * race.
 *
 * The race: two tabs (or the desktop app and a background poller) both see
 * the access token expire and both POST the SAME stored refresh token. One
 * wins and writes the rotated pair to localStorage; the loser is told the
 * jti it sent was consumed. That loser used to call it a dead session and
 * wipe the tokens — including the good ones the winner had just stored, so
 * BOTH tabs bounced to /login. Now the loser re-reads storage: if the token
 * changed underneath it, another tab already refreshed and we simply adopt
 * that result. (The server also honours a just-rotated jti for a short grace
 * window, which closes the same race from the other side.)
 */
async function attemptRefresh(triesLeft: number): Promise<RefreshOutcome> {
  const sent = tokenStore.getRefresh();
  if (!sent) return "dead";

  // Deliberately NO `navigator.onLine` short-circuit. It used to return
  // "transient" here without sending anything — and Chrome on macOS can
  // report `onLine === false` while every request reaches the server
  // (2026-09-24). The refresh then NEVER went out, the access token stayed
  // expired, and the tab sat on "Can't reach the XOP server" until the user
  // wiped site data. Just try: a request that truly can't leave the machine
  // throws below (→ "transient") and spends nothing; one whose response is
  // lost is covered by the server's rotation grace window.
  let res: Response;
  try {
    res = await fetch(`${env.apiUrl}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refresh_token: sent }),
    });
  } catch {
    return "transient"; // network error / DNS / aborted — session untouched
  }

  if (res.ok) {
    let body: { access_token?: string; refresh_token?: string };
    try {
      body = (await res.json()) as typeof body;
    } catch {
      return "transient";
    }
    if (!body.access_token) return "transient";
    // The dashboard `/auth/refresh` ROTATES the refresh token (it consumes
    // the old jti server-side and returns a fresh one), so we MUST persist
    // the new refresh_token too. Saving only the access token meant the
    // next refresh sent the now-revoked token → 401 → forced logout roughly
    // hourly, which silently kills long-lived surfaces like the realtime
    // socket. Persist both.
    if (body.refresh_token) {
      tokenStore.set(body.access_token, body.refresh_token);
    } else {
      tokenStore.setAccess(body.access_token);
    }
    return "ok";
  }

  // 5xx / 503 (upstream identity service unavailable) / 429 / 408 — the
  // server is telling us to come back later, not that we are logged out.
  if (res.status >= 500 || res.status === 408 || res.status === 429) {
    return "transient";
  }

  // 401 / 403 → the refresh token was rejected. Before believing it, give
  // a concurrent tab's rotation a moment to land (see the doc comment).
  if (triesLeft > 0) {
    await delay(ROTATION_RACE_WAIT_MS);
    if (tokenStore.getRefresh() !== sent) return attemptRefresh(triesLeft - 1);
  }
  return "dead";
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Force a session refresh (rotating + persisting BOTH tokens), reporting
 * WHY it failed. Used by `sessionKeepAlive` (which retries "transient"
 * with backoff) and by the AuthProvider.
 */
export function refreshSessionTokens(): Promise<RefreshOutcome> {
  return sharedRefresh();
}

/**
 * Boolean flavour of `refreshSessionTokens`, for the realtime socket: it
 * only wants to know whether to reconnect now, and its own backoff already
 * handles "not yet". Exported because a stream that returns 401/403 must
 * refresh before reconnecting — otherwise a connection dropped after the
 * access token expires can never recover.
 */
export async function refreshAccessToken(): Promise<boolean> {
  return (await sharedRefresh()) === "ok";
}

/**
 * Upload a file as a raw octet-stream body (the real name + mime ride in
 * headers). Same bearer + one-shot 401→refresh→retry as `api()`. Used for
 * chat attachments — kept binary (not multipart) to match the server's
 * single content-type parser. `extraHeaders` lets a caller add markers like
 * `x-voice-note` for a recorded voice message.
 */
export async function apiUpload<T>(
  path: string,
  file: File,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const url = path.startsWith("http") ? path : `${env.apiUrl}${path}`;
  const build = (): RequestInit => ({
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/octet-stream",
      "x-file-name": encodeURIComponent(file.name),
      "x-file-mime": file.type || "application/octet-stream",
      ...clientHeader(),
      ...authHeader(),
      ...extraHeaders,
    },
    body: file,
  });
  let res = await fetch(url, build());
  if (res.status === 401) {
    const outcome = await sharedRefresh();
    if (outcome === "ok") {
      res = await fetch(url, build());
    } else if (outcome === "dead") {
      notifySessionLost({ source: "dashboard-api", status: 401 });
    }
  }
  return parseResponse<T>(res);
}

/**
 * POST a `multipart/form-data` body (bearer-authed, one-shot 401→refresh).
 * The browser sets the multipart `Content-Type` + boundary itself, so we
 * must NOT set it manually. Used for avatar uploads (the dashboard proxy
 * forwards the multipart bytes straight to portal-api's avatar pipeline).
 */
export async function apiUploadForm<T>(path: string, form: FormData): Promise<T> {
  const url = path.startsWith("http") ? path : `${env.apiUrl}${path}`;
  const build = (): RequestInit => ({
    method: "POST",
    credentials: "include",
    headers: { ...clientHeader(), ...authHeader() },
    body: form,
  });
  let res = await fetch(url, build());
  if (res.status === 401) {
    const outcome = await sharedRefresh();
    if (outcome === "ok") {
      res = await fetch(url, build());
    } else if (outcome === "dead") {
      notifySessionLost({ source: "dashboard-api", status: 401 });
    }
  }
  return parseResponse<T>(res);
}

/**
 * GET a binary resource as a Blob, bearer-authenticated (so it works for the
 * auth-gated chat attachment endpoint — an `<img src>` can't carry the
 * bearer, so we fetch + make an object URL instead). One-shot 401→refresh.
 */
export async function apiBlob(path: string): Promise<Blob> {
  const url = path.startsWith("http") ? path : `${env.apiUrl}${path}`;
  const build = (): RequestInit => ({
    credentials: "include",
    headers: { ...clientHeader(), ...authHeader() },
  });
  let res = await fetch(url, build());
  if (res.status === 401 && (await sharedRefresh()) === "ok") {
    res = await fetch(url, build());
  }
  if (!res.ok) {
    // A failed download still answers in JSON, and that body is the whole point
    // of a refusal: "Ask an administrator to raise the limit" is actionable,
    // "Forbidden" is not. Reading it costs nothing and every caller that used to
    // surface `err.message` now surfaces the server's sentence instead.
    const contentType = res.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      try {
        const body = (await res.json()) as {
          error?: unknown;
          message?: unknown;
          code?: unknown;
          details?: unknown;
        };
        const message =
          (typeof body.error === "string" && body.error) ||
          (typeof body.message === "string" && body.message) ||
          res.statusText ||
          `HTTP ${res.status}`;
        throw new ApiError(
          message,
          res.status,
          typeof body.code === "string" ? body.code : undefined,
          body.details,
        );
      } catch (err) {
        // Rethrow OUR error; fall through only when the body was not parseable.
        if (err instanceof ApiError) throw err;
      }
    }
    throw new ApiError(res.statusText || `HTTP ${res.status}`, res.status);
  }
  return res.blob();
}
